import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { FeatureKey, FeatureMap, MemberRole, Permission, PlanLimits, SubscriptionDto } from "@dishfolio/contracts";
import { api, setApiOrg } from "./api.ts";

export type Membership = { id: string; name: string; slug: string; role: MemberRole };
export type Me = {
  user: { id: string; email: string; name: string; emailVerified: boolean; role: string; impersonatedBy: string | null };
  organizations: Membership[];
  /** Set while a platform admin is impersonating this user. */
  impersonator: { id: string; name: string; email: string } | null;
};
export type OrgDetails = { organization: { id: string; name: string; slug: string }; role: MemberRole; permissions: Permission[]; plan: PlanLimits; features: FeatureMap; subscription: SubscriptionDto };

const KEY = "dishfolio.org";

type Ctx = {
  me: Me;
  orgId: string | null;
  org: OrgDetails | undefined;
  setOrgId: (id: string) => void;
  can: (p: Permission) => boolean;
  /** Feature flags (managed by platform admins). */
  feature: (k: FeatureKey) => boolean;
  refresh: () => Promise<void>;
};
const OrgCtx = createContext<Ctx | null>(null);

function readStored(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function OrgProvider({ me, children }: { me: Me; children: ReactNode }) {
  const qc = useQueryClient();
  const initial = (() => {
    const stored = readStored();
    if (stored && me.organizations.some((o) => o.id === stored)) return stored;
    return me.organizations[0]?.id ?? null;
  })();
  const [orgId, setOrgIdState] = useState<string | null>(initial);
  setApiOrg(orgId);
  useEffect(() => {
    setApiOrg(orgId);
    try {
      if (orgId) localStorage.setItem(KEY, orgId);
    } catch {
      /* storage unavailable */
    }
  }, [orgId]);
  const org = useQuery({ queryKey: ["org", orgId], queryFn: () => api.get<OrgDetails>("/organization"), enabled: Boolean(orgId) });
  const value = useMemo<Ctx>(
    () => ({
      me,
      orgId,
      org: org.data,
      setOrgId: (id) => {
        setApiOrg(id);
        setOrgIdState(id);
        void qc.invalidateQueries();
      },
      can: (p) => Boolean(org.data?.permissions.includes(p)),
      feature: (k) => Boolean(org.data?.features?.[k]),
      refresh: async () => {
        await qc.invalidateQueries({ queryKey: ["org"] });
        await qc.invalidateQueries({ queryKey: ["me"] });
      },
    }),
    [me, orgId, org.data, qc],
  );
  return <OrgCtx.Provider value={value}>{children}</OrgCtx.Provider>;
}

export function useOrg(): Ctx {
  const v = useContext(OrgCtx);
  if (!v) throw new Error("useOrg outside OrgProvider");
  return v;
}
